"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getUniqueTimeframes = exports.getOverlappingTimeframe = exports.checkTimeframesOverlap = exports.checkIsSameTimeframe = exports.checkNowIsInTimeframe = void 0;
const moment_1 = __importDefault(require("moment"));
const luxon_1 = require("luxon");
/**
 * Takes a timeframe and determines whether the user's time falls within it.
 * @param {Date|string} start Starting point of timeframe. Must be a valid date, date string or ISO date number. Timeframe must have a start.
 * @param {Date|string|null} end End point of timeframe. Must be a valid date, date string or ISO date number. If null, it means the timeframe has no end.
 */
function checkNowIsInTimeframe(start, end) {
    const s = typeof start === 'string' || typeof start === 'number'
        ? new Date(start).getTime()
        : start.getTime();
    const e = typeof end === 'string' || typeof end === 'number'
        ? new Date(end).getTime()
        : end
            ? end.getTime()
            : null;
    if (isNaN(s))
        throw new Error('Invalid date: ' + start);
    if (e !== null && isNaN(e))
        throw new Error('Invalid date: ' + end);
    const now = new Date().getTime();
    return s <= now && (!e || e > now);
}
exports.checkNowIsInTimeframe = checkNowIsInTimeframe;
/**
 * Takes two timeframes and determines whether they are the same timeframe.
 * @param arg1 First timeframe.
 * @param arg2 Second timeframe.
 */
function checkIsSameTimeframe(arg1, arg2) {
    const one = [
        new Date(arg1.start).getTime(),
        !arg1.end ? null : new Date(arg1.end).getTime(),
    ];
    const two = [
        new Date(arg2.start).getTime(),
        !arg2.end ? null : new Date(arg2.end).getTime(),
    ];
    const isSame = JSON.stringify(one) === JSON.stringify(two);
    return isSame;
}
exports.checkIsSameTimeframe = checkIsSameTimeframe;
/**
 * Takes two timeframes and determines if they overlap at any point (i.e. there are points in time that are inside both timeframes).
 * @param arg1 First timeframe.
 * @param arg2 Second timeframe.
 */
function checkTimeframesOverlap(arg1, arg2) {
    let tf;
    switch (true) {
        case !arg1.end && !arg2.end:
            return true;
        case !arg1.end && arg2.end && true:
            tf = [(0, moment_1.default)(arg2.start), (0, moment_1.default)(arg2.end)];
            if (tf[0].isSameOrAfter(arg1.start) || tf[1].isAfter(arg1.start))
                return true;
            break;
        case !arg2.end && arg2.start && true:
            tf = [(0, moment_1.default)(arg1.start), (0, moment_1.default)(arg1.end)];
            if (tf[0].isSameOrAfter(arg2.start) || tf[1].isAfter(arg2.start))
                return true;
            break;
        case arg2.end && arg1.end && true:
            tf = [(0, moment_1.default)(arg1.start), (0, moment_1.default)(arg1.end)];
            if (tf[0].isSameOrBefore(arg2.start) && tf[1].isAfter(arg2.start))
                return true;
            if (tf[0].isBefore(arg2.end) && tf[1].isSameOrAfter(arg2.end))
                return true;
            tf = [(0, moment_1.default)(arg2.start), (0, moment_1.default)(arg2.end)];
            if (tf[0].isSameOrBefore(arg1.start) && tf[1].isAfter(arg1.start))
                return true;
            if (tf[0].isBefore(arg1.end) && tf[1].isSameOrAfter(arg1.end))
                return true;
            break;
        default:
            throw new Error('Unexpected');
    }
    return false;
}
exports.checkTimeframesOverlap = checkTimeframesOverlap;
function getOverlappingTimeframe(arg1, arg2, options) {
    var _a, _b;
    let overlappingTimeframe = null;
    let overlappingDateTime;
    if (!checkTimeframesOverlap(arg1, arg2)) {
        // Timeframe don't overlap
        overlappingDateTime = null;
    }
    else if (!arg1.end || !arg2.end) {
        // At least one timeframe is open ended
        const start1 = toDateTime(arg1.start).toUTC();
        const start2 = toDateTime(arg2.start).toUTC();
        let end;
        // Check which timeframe ends first
        if (arg1.end !== null) {
            end = toDateTime(arg1.end).toUTC();
        }
        else if (arg2.end !== null) {
            end = toDateTime(arg2.end).toUTC();
        }
        else {
            end = null;
        }
        if (start1 > start2) {
            overlappingDateTime = {
                start: start1,
                end,
            };
        }
        else {
            overlappingDateTime = {
                start: start2,
                end,
            };
        }
    }
    else {
        // Not Open Ended
        const interval1 = luxon_1.Interval.fromDateTimes(toDateTime(arg1.start).toUTC(), toDateTime(arg1.end).toUTC());
        const interval2 = luxon_1.Interval.fromDateTimes(toDateTime(arg2.start).toUTC(), toDateTime(arg2.end).toUTC());
        const overlapInterval = interval1.intersection(interval2);
        overlappingDateTime = overlapInterval
            ? {
                start: overlapInterval.start,
                end: overlapInterval.end,
            }
            : null;
    }
    if (overlappingDateTime === null) {
        // No overlap
        overlappingTimeframe = null;
    }
    else {
        // Format overlap format
        (options === null || options === void 0 ? void 0 : options.asDate) === true
            ? (overlappingTimeframe = {
                start: overlappingDateTime.start.toJSDate(),
                end: overlappingDateTime.end !== null ? (_a = overlappingDateTime.end) === null || _a === void 0 ? void 0 : _a.toJSDate() : null,
            })
            : (overlappingTimeframe = {
                start: overlappingDateTime.start.toISO(),
                end: overlappingDateTime.end !== null ? (_b = overlappingDateTime.end) === null || _b === void 0 ? void 0 : _b.toISO() : null,
            });
    }
    return overlappingTimeframe;
}
exports.getOverlappingTimeframe = getOverlappingTimeframe;
/**
 * Takes string, number or JsDate and converts to luxon DateTime in UTC
 * @param {string | number | Date} date
 * @return {DateTime}
 */
function toDateTime(date) {
    switch (typeof date) {
        case 'string':
            return luxon_1.DateTime.fromISO(date);
        case 'number':
            return luxon_1.DateTime.fromMillis(date);
        default:
            return luxon_1.DateTime.fromJSDate(date);
    }
}
/**
 * Takes array of timeframes and determines unique (distinct) timeframes that are included.
 * @param {Timeframe<string | number | Date>[]} of Array to find unqiue timeframes of.
 * @return {Timeframe[]} Array of unique timeframes which is sorted by start from earliest to latest.
 */
function getUniqueTimeframes(of) {
    const initial = of.map((r) => [
        new Date(r.start).getTime(),
        !r.end ? null : new Date(r.end).getTime(),
    ]);
    if (initial.length < 1)
        return [];
    const res = [...new Set(initial.map((tf) => JSON.stringify(tf)))].map((v) => JSON.parse(v));
    const sorted = res.sort((a, b) => a[0] - b[0]);
    return sorted.map((v) => ({
        start: new Date(v[0]).toISOString(),
        end: v[1] ? new Date(v[1]).toISOString() : null,
    }));
}
exports.getUniqueTimeframes = getUniqueTimeframes;
